<template>
  <div class="occupancy-analytics">
    <Loader :loading="showLoader" />

    <v-toolbar class="custom-toolbar mb-4" flat dense>
      <v-btn icon>
        <v-icon size="40" @click="goBack">
          mdi-arrow-left-bold-circle
        </v-icon>
      </v-btn>
      <v-toolbar-title class="custom-toolbar-title">
        {{ $t('occupancy.occupancy-analytics') }}
      </v-toolbar-title>
      <img :src="logo" class="toolbar-icon" />
    </v-toolbar>

    <v-container fluid class="py-6">
      <template v-if="showCharts">
        <transition name="fade">
          <v-row justify="center" v-if="!showLoader">
            <v-col cols="12" sm="4">
              <v-select v-model="selectedTimeRange" :items="timeRangeOptions" :label="$t('occupancy.time-range')" dense
                outlined item-value="value" item-title="text" class="mb-4"
                @update:modelValue="handleFilterChange"></v-select>
            </v-col>
            <v-col cols="12" sm="4">
              <v-select v-model="selectedCategory" :items="poiCategories" item-title="name" item-value="name"
                return-object :label="$t('occupancy.poi-category')" dense outlined class="mb-4"
                @update:modelValue="handleFilterChange"></v-select>
            </v-col>
            <v-col cols="12" sm="4">
              <v-select v-model="selectedFloor" :items="poiFloors" item-title="name" item-value="name" return-object
                :label="$t('occupancy.floor')" dense outlined class="mb-4"
                @update:modelValue="handleFilterChange"></v-select>
            </v-col>
          </v-row>
        </transition>

        <transition name="fade" mode="out-in">
          <v-row justify="center" v-if="!showLoader">
            <v-col cols="12">
              <v-card class="pa-4 mb-4 heatmap-card">
                <apexchart v-if="!showLoader && isValidHeatmapData" ref="heatmapChart" width="100%" height="350"
                  type="heatmap" :options="heatmapChartOptions" :series="heatmapChartData" />
              </v-card>
            </v-col>
          </v-row>
        </transition>

        <transition name="fade" mode="out-in" v-if="showPoiHeatMap">
          <PoiHeatMap v-if="!showLoader && showPoiHeatMap" :reservations="poiHeatmapData"
            :selectedTimeRange="selectedTimeRange" :selectedCategory="selectedCategory"
            :selectedFloor="selectedFloor" />
        </transition>

        <transition name="fade" mode="out-in" v-if="showPyramidChart">
          <v-row justify="center" v-if="!showLoader && showPyramidChart">
            <v-col cols="12">
              <ReservationsPyramidChart :reservations="reservations" :selected-time-range="selectedTimeRange"
                :selected-category="selectedCategory" :selected-floor="selectedFloor" />
            </v-col>
          </v-row>
        </transition>
      </template>

      <template v-else>
        <v-row justify="center">
          <v-col cols="12" class="text-center">
            <p class="rotate-message"> {{ $t('occupancy.rotate') }}</p>
          </v-col>
        </v-row>
      </template>
    </v-container>
  </div>
</template>

<script>
import { ref, defineComponent, onMounted, onUnmounted, watch } from 'vue'
import Loader from '@/components/general/Loader.vue'
import VueApexCharts from 'vue3-apexcharts'
import ReservationsPyramidChart from '@/components/general/ChildComponents/ReservationsPyramidChart.vue'
import PoiHeatMap from '@/components/general/ChildComponents/PoiHeatMap.vue'
import { getToolbarLogoImage } from '@/assets/Branding/branding.js'
import { categories } from '@/dummydata/categories.js'
import { floors } from '@/dummydata/floors.js'
import moment from 'moment'
import { Device } from '@capacitor/device'
import { ScreenOrientation } from '@capacitor/screen-orientation'
import { useI18n } from 'vue-i18n'
import { computed } from 'vue'


export default defineComponent({
  name: 'OccupancyAnalytics',
  components: {
    Loader,
    apexchart: VueApexCharts,
    ReservationsPyramidChart,
    PoiHeatMap,
  },
  setup() {
    const showLoader = ref(true)
    const logo = ref('')
    const heatmapChart = ref(null)
    const showPyramidChart = ref(false)
    const showPoiHeatMap = ref(false)
    const poiHeatmapData = ref([])
    const reservations = ref([])
    const isMobile = ref(false)
    const showCharts = ref(true)
    const { t } = useI18n()

    const tomorrow = moment().add(1, 'day')
    const isTomorrowWeekend = tomorrow.isoWeekday() === 6 || tomorrow.isoWeekday() === 7

    const timeRangeOptions = ref([
      { text: 'Today', value: 'today' },
      ...(isTomorrowWeekend ? [] : [{ text: 'Tomorrow', value: 'tomorrow' }]),
      { text: 'Yesterday', value: 'yesterday' },
      { text: 'This Week', value: 'thisWeek' },
      { text: 'Last Week', value: 'lastWeek' },
      { text: 'Current Month', value: 'currentMonth' },
      { text: 'Last Month', value: 'lastMonth' },
      { text: 'Next Month', value: 'nextMonth' },
    ])

    const poiCategories = ref([{ name: 'All Categories' }])
    const poiFloors = ref([{ name: 'All Floors' }])

    const selectedTimeRange = ref('today')
    const selectedCategory = ref(poiCategories.value[0])
    const selectedFloor = ref(poiFloors.value[0])
    const isValidHeatmapData = computed(() => {
      return (
        Array.isArray(heatmapChartData.value) &&
        heatmapChartData.value?.length > 0 &&
        heatmapChartData.value?.every(series =>
          Array.isArray(series?.data) &&
          series.data.length > 0 &&
          series.data.every(slot => slot && typeof slot.x !== 'undefined' && typeof slot.y !== 'undefined')
        )
      )
    })
    const goBack = () => {
      try {
        window.history.back()
      } catch {
        // Gracefully ignore navigation errors
      }
    }

    const heatmapChartOptions = ref({
      chart: {
        id: 'heatmap-chart',
        toolbar: { show: true },
        animations: {
          enabled: true,
          easing: 'easeinout',
          speed: 800,
          animateGradually: { enabled: true, delay: 150 },
          dynamicAnimation: { enabled: true, speed: 350 },
        },
      },
      dataLabels: { enabled: true },
      xaxis: { type: 'category', title: { text: 'POI Category' } },
      yaxis: { title: { text: 'Time Slots' } },
      plotOptions: {
        heatmap: {
          colorScale: {
            ranges: [
              { from: 0, to: 3, color: '#00A100', name: 'Low' },
              { from: 4, to: 7, color: '#FFB200', name: 'Medium' },
              { from: 8, to: 100, color: '#FF0000', name: 'High' },
            ],
          },
        },
      },
    })

    const heatmapChartData = ref([])

    const getNextMonthDates = () => {
      try {
        const startDate = moment().add(1, 'month').startOf('month')
        const endDate = moment().add(1, 'month').endOf('month')
        const totalDays = endDate.diff(startDate, 'days') + 1
        return Array.from({ length: totalDays }, (_, i) =>
          startDate.clone().add(i, 'days').format('DD-MM')
        ).filter(date => {
          const dayOfWeek = moment(date, 'DD-MM').isoWeekday()
          return dayOfWeek !== 6 && dayOfWeek !== 7
        })
      } catch {
        return []
      }
    }

    const getMonthDates = (startDate, endDate) => {
      try {
        if (!moment.isMoment(startDate) || !moment.isMoment(endDate)) return []
        const totalDays = endDate.diff(startDate, 'days') + 1
        return Array.from({ length: totalDays }, (_, i) =>
          startDate.clone().add(i, 'days').format('DD-MM')
        ).filter(date => {
          const dayOfWeek = moment(date, 'DD-MM').isoWeekday()
          return dayOfWeek !== 6 && dayOfWeek !== 7
        })
      } catch {
        return []
      }
    }

    const checkDevice = async () => {
      try {
        const info = await Device.getInfo()
        isMobile.value = info?.operatingSystem === 'ios' || info?.operatingSystem === 'android'
      } catch {
        isMobile.value = false // Fallback to non-mobile
      }
    }

    const handleOrientationChange = async () => {
      try {
        const orientation = await ScreenOrientation.orientation()
        showCharts.value = !(isMobile.value && orientation?.type?.includes('portrait'))
      } catch {
        showCharts.value = true // Fallback to showing charts
      }
    }

    const filterReservations = () => {
      try {
        if (!Array.isArray(reservations.value)) return []

        let startDate, endDate
        switch (selectedTimeRange.value) {
          case 'today':
            startDate = moment().startOf('day')
            endDate = moment().endOf('day')
            break
          case 'yesterday':
            startDate = moment().subtract(1, 'day').startOf('day')
            endDate = moment().subtract(1, 'day').endOf('day')
            break
          case 'tomorrow':
            startDate = moment().add(1, 'day').startOf('day')
            endDate = moment().add(1, 'day').endOf('day')
            break
          case 'thisWeek':
            startDate = moment().startOf('week')
            endDate = moment().endOf('week')
            break
          case 'lastWeek':
            startDate = moment().subtract(1, 'week').startOf('week')
            endDate = moment().subtract(1, 'week').endOf('week')
            break
          case 'currentMonth':
            startDate = moment().startOf('month')
            endDate = moment().endOf('month')
            break
          case 'lastMonth':
            startDate = moment().subtract(1, 'month').startOf('month')
            endDate = moment().subtract(1, 'month').endOf('month')
            break
          case 'nextMonth':
            startDate = moment().add(1, 'month').startOf('month')
            endDate = moment().add(1, 'month').endOf('month')
            break
          default:
            return []
        }

        return reservations.value.filter(reservation => {
          if (!reservation?.startTime) return false
          const reservationDate = moment(reservation.startTime)
          return reservationDate.isValid() && reservationDate.isBetween(startDate, endDate, null, '[]')
        }).filter(reservation => {
          if (selectedCategory.value?.name && selectedCategory.value.name !== 'All Categories') {
            return reservation?.poiCategory === selectedCategory.value.name
          }
          return true
        }).filter(reservation => {
          if (selectedFloor.value?.name && selectedFloor.value.name !== 'All Floors') {
            return reservation?.floorName === selectedFloor.value.name
          }
          return true
        })
      } catch {
        return []
      }
    }

    const processHeatmapData = () => {
      try {
        const filteredReservations = filterReservations()
        if (!filteredReservations.length) {
          heatmapChartData.value = []
          return
        }

        let yAxisLabels, dataByCategoryAndTimeOrDate
        if (['today', 'yesterday', 'tomorrow'].includes(selectedTimeRange.value)) {
          yAxisLabels = Array.from({ length: 15 }, (_, i) => `${i + 6}:00`)
          dataByCategoryAndTimeOrDate = {}

          poiCategories.value.forEach(category => {
            if (category?.name && category.name !== 'All Categories') {
              dataByCategoryAndTimeOrDate[category.name] = yAxisLabels.map(time => ({ x: time, y: 0 }))
            }
          })

          filteredReservations.forEach(reservation => {
            if (!reservation?.poiCategory || !reservation?.startTime) return
            const category = reservation.poiCategory
            const startTime = moment(reservation.startTime)
            if (!startTime.isValid()) return
            const formattedTime = startTime.format('H:00')
            const categoryData = dataByCategoryAndTimeOrDate[category]
            if (categoryData) {
              const timeSlot = categoryData.find(slot => slot.x === formattedTime)
              if (timeSlot) timeSlot.y += 1
            }
          })
        } else {
          const startDate = moment(filteredReservations[0]?.startTime).startOf('day')
          const endDate = moment(filteredReservations[filteredReservations.length - 1]?.endTime).endOf('day')
          if (!startDate.isValid() || !endDate.isValid()) {
            heatmapChartData.value = []
            return
          }

          yAxisLabels = selectedTimeRange.value === 'nextMonth'
            ? getNextMonthDates()
            : getMonthDates(startDate, endDate)
          dataByCategoryAndTimeOrDate = {}

          poiCategories.value.forEach(category => {
            if (category?.name && category.name !== 'All Categories') {
              dataByCategoryAndTimeOrDate[category.name] = yAxisLabels.map(date => ({ x: date, y: 0 }))
            }
          })

          filteredReservations.forEach(reservation => {
            if (!reservation?.poiCategory || !reservation?.startTime) return
            const category = reservation.poiCategory
            const reservationDate = moment(reservation.startTime)
            if (!reservationDate.isValid()) return
            const formattedDate = reservationDate.format('DD-MM')
            const categoryData = dataByCategoryAndTimeOrDate[category]
            if (categoryData) {
              const dateSlot = categoryData.find(slot => slot.x === formattedDate)
              if (dateSlot) dateSlot.y += 1
            }
          })
        }
        heatmapChartData.value = Object.keys(dataByCategoryAndTimeOrDate).map(category => {
          const data = Array.isArray(dataByCategoryAndTimeOrDate[category])
            ? dataByCategoryAndTimeOrDate[category].filter(p => typeof p.y === 'number')
            : [];

          return {
            name: category,
            data
          };
        }).filter(series => Array.isArray(series.data) && series.data.length > 0);


        heatmapChartOptions.value.yaxis.title.text =
          ['today', 'yesterday', 'tomorrow'].includes(selectedTimeRange.value) ? 'Time Slots' : 'Dates'
      } catch {
        heatmapChartData.value = [] // Graceful fallback
      }
    }

    const handleFilterChange = () => {
      try {
        showLoader.value = true
        showPyramidChart.value = false
        showPoiHeatMap.value = false

        setTimeout(() => {
          try {
            showPoiHeatMap.value = selectedCategory.value?.name !== 'All Categories'
            if (showPoiHeatMap.value) {
              poiHeatmapData.value = filterReservations()
            }
            updateChart()
          } catch {
            poiHeatmapData.value = []
            showLoader.value = false
          }
        }, 500)
      } catch {
        showLoader.value = false
      }
    }

    const updateChart = () => {
      try {
        processHeatmapData()
        if (heatmapChart.value?.updateSeries) {
          heatmapChart.value.updateSeries(heatmapChartData.value)
        }
        showPyramidChart.value = true
      } catch {
        heatmapChartData.value = []
      } finally {
        showLoader.value = false
      }
    }

    onMounted(async () => {
      try {
        showLoader.value = true

        // Load logo
        try {
          logo.value = await getToolbarLogoImage()
        } catch {
          logo.value = '' // Fallback to empty string
        }

        // Load categories and floors
        try {
          poiCategories.value = [{ name: 'All Categories' }, ...categories.flat()]
          poiFloors.value = [{ name: 'All Floors' }, ...floors.flat()]
        } catch {
          poiCategories.value = [{ name: 'All Categories' }]
          poiFloors.value = [{ name: 'All Floors' }]
        }

        // Load reservations
        try {
          const reservationsData = await import('@/dummydata/reservations.json')
          reservations.value = Array.isArray(reservationsData?.reservations) ? reservationsData.reservations : []
        } catch {
          reservations.value = []
        }

        // Initialize device and orientation
        await checkDevice()
        await handleOrientationChange()

        // Add orientation listener
        try {
          ScreenOrientation.addListener('screenOrientationChange', handleOrientationChange)
        } catch {
          // Gracefully ignore listener errors
        }

        // Update charts
        updateChart()
        showPyramidChart.value = true
      } catch {
        // Ensure component remains functional
        reservations.value = []
        heatmapChartData.value = []
        poiHeatmapData.value = []
      } finally {
        showLoader.value = false
      }
    })

    onUnmounted(() => {
      try {
        ScreenOrientation.removeAllListeners()
      } catch {
        // Gracefully ignore cleanup errors
      }
    })

    watch([selectedTimeRange, selectedCategory, selectedFloor], () => {
      try {
        handleFilterChange()
      } catch {
        // Gracefully ignore watch errors
      }
    }, { deep: true })

    return {
      logo,
      showLoader,
      heatmapChartOptions,
      heatmapChartData,
      timeRangeOptions,
      poiCategories,
      poiFloors,
      selectedTimeRange,
      selectedCategory,
      selectedFloor,
      reservations,
      showPyramidChart,
      showPoiHeatMap,
      poiHeatmapData,
      showCharts,
      goBack,
      isValidHeatmapData
    }
  },
})
</script>

<style scoped>
.occupancy-analytics {
  padding: 16px;
  background: #f5f5f5;
  border-radius: 8px;
  padding: 1rem;
}

.custom-toolbar {
  background-color: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(10px);
  color: #333;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  border-radius: 8px;
  padding: 8px 16px;
  display: flex;
  align-items: center;
}

@supports (-webkit-touch-callout: none) {
  .custom-toolbar {
    position: sticky;
    top: 0;
    z-index: 1000;
  }
}

.custom-toolbar-title {
  font-family: 'Poppins', sans-serif;
  font-weight: 600;
  font-size: 1.5rem;
  color: #333;
  margin-left: 10px;
}

.toolbar-icon {
  height: 30px;
}

.heatmap-card {
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.2);
  border-radius: 12px;
  transition: box-shadow 0.3s ease, transform 0.3s ease;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.5s ease-in-out;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}

.v-select .v-input__control {
  font-weight: 600;
  color: #333;
  text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.2);
}

.v-select .v-input__control:hover {
  color: #000;
}

.v-select .v-input__control:focus {
  box-shadow: 0 0 5px rgba(0, 0, 0, 0.4);
  outline: none;
}

.rotate-message {
  font-size: 1.5rem;
  color: #555;
  font-weight: 600;
  margin-top: 20%;
  text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.2);
}
</style>
<!-- OccupancyAnalytics Component
This component provides a comprehensive view of occupancy analytics through various charts and filters. Users can interact with the component to visualize data based on different time ranges, categories, and floors.

The component includes a toolbar with a back button and a title, along with a logo. The toolbar allows users to navigate back to the previous page.

The main content area contains a loader that indicates data loading status. Once the data is loaded, users can interact with several filters to customize the data displayed in the charts. The filters include:
1. Time Range: Allows users to select a specific time range (e.g., Today, Tomorrow, This Week).
2. POI Category: Allows users to filter data based on different categories of points of interest.
3. Floor: Allows users to filter data based on different floors.

The component displays various charts based on the selected filters:
1. Heatmap Chart: Visualizes data in a heatmap format, showing the density of occupancy over time or dates.
2. POI HeatMap: Displays a heatmap specific to the selected category.
3. TreeMap Chart: Visualizes reservations in a tree map format.
4. Pyramid Chart: Displays data in a pyramid chart format.
5. Donut Chart: Shows data in a donut chart format, available only when 'All Categories' is selected.

The component provides visual feedback through transitions and animations, enhancing the user experience. If the device is in portrait mode, a message prompts users to rotate their device to landscape mode for better viewing.

Users can leverage this component to gain insights into occupancy patterns, make informed decisions, and optimize space utilization based on the visualized data. -->
